import { useMutation } from '@tanstack/react-query'
import {
  applyImport,
  downloadImportTemplate,
  previewImport,
} from '@/services/api'
import { invalidateQueries, queryClient } from '@/lib/query'

/**
 * Row-level issue surfaced to the UI. Mirrors api/internal/importer.RowError.
 */
export interface ImportRowError {
  sheet?: string
  row?: number
  column?: string
  message?: string
}

/** One building plan entry in the preview response. */
export interface ImportBuildingPlan {
  row?: number
  name: string
  address?: string
  action: 'create' | 'skip'
  id?: string
  existing_id?: string
}

/** One room plan entry in the preview response. */
export interface ImportRoomPlan {
  row?: number
  building: string
  name: string
  floor: number
  action: 'create' | 'skip'
  id?: string
  existing_id?: string
  resolved_building_id?: string
}

export interface ImportBuildingSection {
  to_create: ImportBuildingPlan[]
  to_skip: ImportBuildingPlan[]
  errors: ImportRowError[]
}

export interface ImportRoomSection {
  to_create: ImportRoomPlan[]
  to_skip: ImportRoomPlan[]
  errors: ImportRowError[]
}

export interface ImportSummary {
  buildings_create: number
  buildings_skip: number
  rooms_create: number
  rooms_skip: number
  errors_total: number
}

export interface ImportBatchError {
  sheet: string
  row: number
  id: string
  message: string
}

export interface ImportPreview {
  buildings: ImportBuildingSection
  rooms: ImportRoomSection
  summary: ImportSummary
}

export interface ImportResult extends ImportPreview {
  batch_errors?: ImportBatchError[]
}

/**
 * Mutation that POSTs the file to the preview endpoint and returns the
 * preview JSON. The UI uses this for Step 3 of the Imports flow.
 */
export function usePreviewImport(orgId: string) {
  return useMutation({
    mutationFn: (file: File) => previewImport<ImportPreview>(orgId, file),
  })
}

/**
 * Mutation that POSTs the file to the apply endpoint and invalidates
 * the buildings + rooms caches on success so existing list pages
 * pick up the new data.
 */
export function useApplyImport(orgId: string) {
  return useMutation({
    mutationFn: (file: File) => applyImport<ImportResult>(orgId, file),
    onSuccess: () => {
      // Invalidate the per-org buildings list and any room cache —
      // room caches are keyed by buildingId (['rooms', {buildingId}])
      // plus the org-wide ['rooms', 'all'] key. A shallow ['rooms']
      // prefix invalidation matches both.
      invalidateQueries.buildings(orgId)
      queryClient.invalidateQueries({ queryKey: ['rooms'] })
    },
  })
}

/**
 * Wrapper around the raw download helper so callers can express intent
 * ("download the template for this org") and potentially retry / show
 * loading state. Not a react-query mutation because the browser's
 * download UI is itself the side-effect — there's no meaningful
 * response body the UI needs to render.
 */
export async function triggerTemplateDownload(orgId: string): Promise<void> {
  return downloadImportTemplate(orgId)
}
